import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../utils';

export const useWorkTracking = (appointment, onUpdate, t) => {
  const [workInProgress, setWorkInProgress] = useState(appointment.status === "in_progress");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startingWork, setStartingWork] = useState(false);
  const [endingWork, setEndingWork] = useState(false);
  const [showEndWorkForm, setShowEndWorkForm] = useState(false);
  const [endWorkStatus, setEndWorkStatus] = useState("completed");
  const [endWorkReason, setEndWorkReason] = useState("");
  const [endWorkReasonCategory, setEndWorkReasonCategory] = useState("");
  const [gettingLocation, setGettingLocation] = useState(false);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (appointment.status === "in_progress" && appointment.work_started_at) {
      const startTime = new Date(appointment.work_started_at).getTime();
      const updateElapsed = () => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      };
      updateElapsed();
      interval = setInterval(updateElapsed, 1000);
      setWorkInProgress(true);
    } else {
      setWorkInProgress(false);
      setElapsedTime(0);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [appointment.status, appointment.work_started_at]);

  const formatElapsedTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null });
        return;
      }
      setGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGettingLocation(false);
          resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        },
        (error) => {
          console.warn("GPS non disponibile:", error.message);
          setGettingLocation(false);
          resolve({ latitude: null, longitude: null });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  // Lock minimo per evitare doppi click accidentali su mobile (latenza di rete).
  // Quando setEndingWork(true) o setStartingWork(true), garantiamo che restino true
  // almeno LOCK_MS millisecondi anche se l'API risponde piu' velocemente.
  const LOCK_MS = 3000;

  const releaseAfterLock = (setter, startedAt) => {
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, LOCK_MS - elapsed);
    if (remaining === 0) {
      setter(false);
    } else {
      setTimeout(() => setter(false), remaining);
    }
  };

  const handleStartWork = async () => {
    const startedAt = Date.now();
    setStartingWork(true);
    toast.dismiss();
    try {
      const { latitude, longitude } = await getCurrentLocation();
      const res = await axios.post(`${API}/appointments/${appointment.id}/start-work`, { latitude, longitude });
      if (res.data?.already_started) {
        toast.info(res.data?.message || "Lavoro gia' avviato.");
      } else {
        toast.success(t('appointment.messages.workStarted') || 'Lavoro iniziato!');
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Start work error:", err);
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      releaseAfterLock(setStartingWork, startedAt);
    }
  };

  const handleEndWork = async () => {
    if (endWorkStatus === "not_completed") {
      if (!endWorkReasonCategory) {
        toast.error("Seleziona la causale del mancato completamento");
        return;
      }
      if (!endWorkReason.trim()) {
        toast.error("I dettagli sulla causale sono obbligatori");
        return;
      }
    }
    const startedAt = Date.now();
    setEndingWork(true);
    // Pulisce eventuali toast errore precedenti (es. "Il lavoro non e' stato avviato"
    // rimasto appiccicato da retry precedenti) per evitare confusione visiva.
    toast.dismiss();
    try {
      const { latitude, longitude } = await getCurrentLocation();
      const categoryLabels = {
        cliente_assente: "Cliente assente",
        veicolo_non_disponibile: "Veicolo non disponibile",
        materiale_mancante: "Materiale/Dispositivo mancante",
        dato_errato: "Dati errati o mancanti",
        impossibilita_tecnica: "Impossibilita tecnica",
        annullato_cliente: "Annullato dal cliente",
        condizioni_meteo: "Condizioni meteo",
        altro: "Altro",
      };
      const fullReason = endWorkReasonCategory
        ? `[${categoryLabels[endWorkReasonCategory] || endWorkReasonCategory}] ${endWorkReason || ""}`.trim()
        : (endWorkReason || null);
      const res = await axios.post(`${API}/appointments/${appointment.id}/end-work`, {
        latitude, longitude, status: endWorkStatus, reason: fullReason,
        reason_category: endWorkReasonCategory || null,
      });
      if (res.data?.already_ended) {
        toast.info(res.data?.message || "Il lavoro era gia' stato terminato. Stato aggiornato.");
      } else {
        toast.success(t('appointment.messages.workEnded') || 'Lavoro terminato!');
      }
      setShowEndWorkForm(false);
      setEndWorkStatus("completed");
      setEndWorkReason("");
      setEndWorkReasonCategory("");
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("End work error:", err);
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      releaseAfterLock(setEndingWork, startedAt);
    }
  };

  return {
    workInProgress, elapsedTime, formatElapsedTime,
    startingWork, endingWork, gettingLocation,
    showEndWorkForm, setShowEndWorkForm,
    endWorkStatus, setEndWorkStatus,
    endWorkReason, setEndWorkReason,
    endWorkReasonCategory, setEndWorkReasonCategory,
    handleStartWork, handleEndWork,
  };
};
